import { useTranslation } from 'react-i18next';
import { Grid, Box, Button, TextField, Typography, Divider, alpha, Alert, AlertTitle, Stepper, Step, StepLabel } from '@mui/material';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import * as XLSX from 'xlsx';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { AutoFixHigh, CheckCircle, CloudUpload, NavigateNext, Save, VerifiedUser } from '@mui/icons-material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { importMembers, validateImportMembers, ImportValidationResult } from 'src/shared/services/formMemberService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { StyledMultiStepConnector } from 'src/shared/components/multistepper/styledMultiStepConnector';
import { ImportMembers } from '../models/importMembers';
import { MemberImportCancelButton } from '../components/memberImportCancelButton';
import { MemberImportDownloadButton } from '../components/memberImportDownloadButton';
import {
  MemberImportColumnMapping,
  ColumnMapping,
  createInitialMappings,
  isProfileMapping,
  getProfileCategory,
} from '../components/memberImportColumnMapping';
import { importFieldDefinitions } from '../models/importFieldDefinitions';

export interface ExcelRow {
  [key: string]: string | number;
}

export const MemberImportPage = () => {
  const { t } = useTranslation(['member', 'common']);
  const navigate = useNavigate();
  const { currentUser } = useGlobals();
  const [loading, setLoading] = useState(false);
  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();
  const isMounted = useIsMounted();

  // Förvälja förening om användaren bara har en
  useEffect(() => {
    if (!association && currentUser?.associations?.length === 1) {
      setAssociation(currentUser.associations[0]);
    }
  }, [currentUser, association]);

  const [activeStep, setActiveStep] = useState(0);

  // Rå Excel-data (oförändrad)
  const [rawExcelHeaders, setRawExcelHeaders] = useState<string[]>([]);
  const [rawExcelRows, setRawExcelRows] = useState<ExcelRow[]>([]);

  // Mappning
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);

  // Transformerad data (efter mappning)
  const [mappedHeaders, setMappedHeaders] = useState<string[]>([]);
  const [gridColumns, setGridColumns] = useState<GridColDef[]>([]);
  const [displayData, setDisplayData] = useState<ExcelRow[]>([]);
  const [rawData, setRawData] = useState<string[]>([]);

  const fileUploadRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<Blob>();
  const [fileName, setFileName] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);

  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);

  // Formatera Date-objekt till YYYY-MM-DD
  const formatDateValue = (val: unknown): string | number => {
    if (val instanceof Date) {
      const y = val.getFullYear();
      const m = String(val.getMonth() + 1).padStart(2, '0');
      const d = String(val.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return val as string | number;
  };

  const processWorksheet = (ws: XLSX.WorkSheet) => {
    // cellDates: true på XLSX.read gör att datum blir JS Date-objekt
    const jsonData = XLSX.utils.sheet_to_json<ExcelRow>(ws, { defval: '' });

    if (jsonData.length === 0) return;

    const headers = Object.keys(jsonData[0]);

    // Konvertera Date-objekt till YYYY-MM-DD strängar
    const formattedData = jsonData.map((row) => {
      const formatted: ExcelRow = {};
      headers.forEach((h) => {
        formatted[h] = formatDateValue(row[h]);
      });
      return formatted;
    });

    setRawExcelHeaders(headers);
    setRawExcelRows(formattedData);

    // Skapa initiala mappningar via synonym-matchning
    const initialMappings = createInitialMappings(headers);
    setColumnMappings(initialMappings);

    // Gå till mappningssteget
    setActiveStep(1);
  };

  useEffect(() => {
    let fileReader: FileReader;
    let isCancel = false;
    if (file) {
      setLoading(true);
      fileReader = new FileReader();
      fileReader.onload = () => {
        const { result } = fileReader;
        if (result && !isCancel) {
          const wb = XLSX.read(result, { type: 'binary', cellDates: true });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          processWorksheet(ws);
        }
      };
      fileReader.readAsBinaryString(file);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
    return () => {
      isCancel = true;
      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    };
  }, [file]);

  const changeHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.target.files) {
      const newFile = e.target.files[0];
      setFile(newFile);
      setFileName(newFile.name);
    }
  };

  const onSelectFile = () => {
    if (fileUploadRef && fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (association) setIsDragging(true);
    },
    [association],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!association) return;
      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) {
        setFile(droppedFile);
        setFileName(droppedFile.name);
      }
    },
    [association],
  );

  /** Tillämpa mappningen och bygg transformerad data för preview/validate/import */
  const applyMapping = () => {
    const activeMappings = columnMappings.filter((m) => m.fieldKey);
    const standardMappings = activeMappings.filter((m) => !isProfileMapping(m.fieldKey));
    const profileMappings = activeMappings.filter((m) => isProfileMapping(m.fieldKey));
    const hasProfiles = profileMappings.length > 0;

    // Kolumnnamn som API:t förväntar sig (+ "profiles" om det finns profilkolumner)
    const headers = standardMappings.map((m) => m.fieldKey as string);
    if (hasProfiles) headers.push('profiles');
    setMappedHeaders(headers);

    // Bygg grid-kolumner
    const narrowFields = ['memberNr', 'gender', 'zipcode', 'phone', 'phoneOther'];
    const wideFields = ['comment', 'profiles', 'address', 'email'];
    const getMinWidth = (key: string) => {
      if (narrowFields.includes(key)) return 60;
      if (wideFields.includes(key)) return 150;
      return 90;
    };
    const getFlex = (key: string) => {
      if (narrowFields.includes(key)) return 0.5;
      if (wideFields.includes(key)) return 1.5;
      return 1;
    };
    const cols: GridColDef[] = standardMappings.map((m) => {
      const fieldDef = importFieldDefinitions.find((f) => f.key === m.fieldKey);
      const key = m.fieldKey as string;
      return { field: key, headerName: fieldDef?.label || key, minWidth: getMinWidth(key), flex: getFlex(key) };
    });
    if (hasProfiles) {
      cols.push({ field: 'profiles', headerName: 'Profiler', minWidth: 150, flex: 1.5 });
    }
    setGridColumns(cols);

    // Transformera rader
    const result: ExcelRow[] = [];
    const tempRawData: string[] = [];

    rawExcelRows.forEach((row, i) => {
      const mappedRow: ExcelRow = { id: i + 1 };

      // Standardfält
      standardMappings.forEach((m) => {
        mappedRow[m.fieldKey as string] = row[m.excelHeader];
      });

      // Slå ihop profilkolumner till "Kategori|Profil, Kategori|Profil"
      if (hasProfiles) {
        const profileParts: string[] = [];
        profileMappings.forEach((m) => {
          const category = getProfileCategory(m.fieldKey as string);
          const cellValue = String(row[m.excelHeader] ?? '').trim();
          if (cellValue) {
            // Stöd kommaseparerade värden i samma cell, t.ex. "Boule, Kajak"
            cellValue.split(',').forEach((v) => {
              const trimmed = v.trim();
              if (trimmed) profileParts.push(`${category}|${trimmed}`);
            });
          }
        });
        mappedRow.profiles = profileParts.join(', ');
      }

      result.push(mappedRow);

      // Bygg CSV-rad för API:t
      const values = headers.map((h) => {
        const val = String(mappedRow[h] ?? '')
          .replace(/\n/g, ' ')
          .replace(/\r/g, '');
        return val;
      });
      tempRawData.push(values.join(';'));
    });

    setDisplayData(result);
    setRawData(tempRawData);
  };

  const handleProceedFromMapping = () => {
    applyMapping();
    setActiveStep(2);
  };

  // Kolla om obligatoriska fält saknas
  const requiredFields = importFieldDefinitions.filter((f) => f.required);
  const mappedFieldKeys = new Set(columnMappings.filter((m) => m.fieldKey).map((m) => m.fieldKey));
  const hasAllRequired = requiredFields.every((f) => mappedFieldKeys.has(f.key));

  const getImportData = (): ImportMembers => ({
    data: rawData,
    columnNames: mappedHeaders.join(','),
    associationId: association?.localAss_id || 0,
    districtId: district?.countyAss_id || association?.countyAss_id || 0,
    fileName: fileName || 'unknown',
  });

  const handleValidate = async () => {
    setLoading(true);
    try {
      const result = await validateImportMembers(getImportData());
      if (isMounted()) {
        setValidationResult(result.data);
        setActiveStep(3);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  // Hjälpfunktion: uppdatera en kolumn och returnera nytt data
  const applyColumnTransform = (curDisplay: ExcelRow[], curRaw: string[], fieldKey: string, transform: (val: string) => string) => {
    const colIndex = mappedHeaders.indexOf(fieldKey);
    if (colIndex === -1) return { newDisplay: curDisplay, newRaw: curRaw };

    const newDisplay = curDisplay.map((row) => ({ ...row, [fieldKey]: transform(String(row[fieldKey] ?? '')) }));
    const newRaw = curRaw.map((line) => {
      const parts = line.split(';');
      if (colIndex < parts.length) parts[colIndex] = transform(parts[colIndex]);
      return parts.join(';');
    });
    return { newDisplay, newRaw };
  };

  const applyColumnIfEmpty = (curDisplay: ExcelRow[], curRaw: string[], fieldKey: string, value: string) => {
    const colIndex = mappedHeaders.indexOf(fieldKey);
    if (colIndex === -1) return { newDisplay: curDisplay, newRaw: curRaw };

    const newDisplay = curDisplay.map((row) => {
      const current = String(row[fieldKey] ?? '').trim();
      return current ? row : { ...row, [fieldKey]: value };
    });
    const newRaw = curRaw.map((line) => {
      const parts = line.split(';');
      if (colIndex < parts.length && !parts[colIndex].trim()) parts[colIndex] = value;
      return parts.join(';');
    });
    return { newDisplay, newRaw };
  };

  // Validera med specifikt data (inte stale state)
  const validateWithData = async (newRaw: string[], newDisplay: ExcelRow[]) => {
    setDisplayData(newDisplay);
    setRawData(newRaw);
    setLoading(true);
    try {
      const importData: ImportMembers = {
        data: newRaw,
        columnNames: mappedHeaders.join(','),
        associationId: association?.localAss_id || 0,
        districtId: district?.countyAss_id || association?.countyAss_id || 0,
        fileName: fileName || 'unknown',
      };
      const result = await validateImportMembers(importData);
      if (isMounted()) setValidationResult(result.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleFixZipcode = () => {
    const { newDisplay, newRaw } = applyColumnTransform(displayData, rawData, 'zipcode', (val) => val.replace(/\s/g, ''));
    validateWithData(newRaw, newDisplay);
  };

  const handleFixPhone = () => {
    const fixPhoneNr = (val: string) => {
      const trimmed = val.trim();
      if (trimmed.startsWith('+46')) return `0${trimmed.substring(3).replace(/\s/g, '')}`;
      if (trimmed.startsWith('0046')) return `0${trimmed.substring(4).replace(/\s/g, '')}`;
      if (/^[1-9]\d/.test(trimmed)) return `0${trimmed}`;
      return val;
    };
    let { newDisplay, newRaw } = applyColumnTransform(displayData, rawData, 'phone', fixPhoneNr);
    ({ newDisplay, newRaw } = applyColumnTransform(newDisplay, newRaw, 'phoneOther', fixPhoneNr));
    validateWithData(newRaw, newDisplay);
  };

  const [admDateInput, setAdmDateInput] = useState('');

  const handleFixAdmissionDate = () => {
    if (!admDateInput) return;
    const formatted = admDateInput.replace(/-/g, '');
    const { newDisplay, newRaw } = applyColumnIfEmpty(displayData, rawData, 'admissionDate', formatted);
    validateWithData(newRaw, newDisplay);
  };

  const postImportToServer = async () => {
    setLoading(true);
    try {
      await importMembers(getImportData());
      if (isMounted()) {
        toast.info(<>{t('member:importMember.importInProgress')}</>, { autoClose: 5000 });
        navigate(RoutesEnum.ImportMembersSessions);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const resetAll = () => {
    setFile(undefined);
    setRawExcelHeaders([]);
    setRawExcelRows([]);
    setColumnMappings([]);
    setMappedHeaders([]);
    setDisplayData([]);
    setRawData([]);
    setGridColumns([]);
    setValidationResult(null);
    setActiveStep(0);
  };

  // Kolla om en cell har valideringsfel
  const getCellClassName = (field: string, rowNumber: number) => {
    if (!validationResult) return '';
    const row = validationResult.rows.find((r) => r.rowNumber === rowNumber);
    if (!row) return '';
    const issue = row.issues.find((iss) => iss.field === field);
    if (!issue) return '';
    return issue.severity === 'error' ? 'cell-error' : 'cell-warning';
  };

  const stepLabels = [
    t('member:importMember.stepUpload'),
    t('member:importMember.stepMapping'),
    t('member:importMember.stepPreview'),
    t('member:importMember.stepVerify'),
    t('member:importMember.stepImport'),
  ];

  return (
    <FormLoaderWrapper loading={loading}>
      <Box
        sx={{
          '& .MuiDataGrid-root': { fontSize: '0.75rem' },
          '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', fontWeight: 600 },
          '& .cell-error': { backgroundColor: (thm) => alpha(thm.palette.error.light, 0.2) },
          '& .cell-warning': { backgroundColor: (thm) => alpha(thm.palette.warning.light, 0.2) },
        }}
      >
        {/* Stepper */}
        <Box sx={{ p: 2, pb: 0 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }} connector={<StyledMultiStepConnector />}>
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider />

        {/* Steg 0: Välj förening + ladda upp fil */}
        {activeStep === 0 && (
          <>
            <MemberImportDownloadButton />
            <Box sx={{ p: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
                2. {t('member:importMember.sectionUpload')}
              </Typography>
              <Grid container spacing={2}>
                <AssociationAndDistrictSelect
                  useGridLayout
                  gridXs={6}
                  gridLg={4}
                  hideDistrict={currentUser?.districts.length === 0}
                  initialAssociationId={currentUser?.associations?.length === 1 ? currentUser.associations[0].localAss_id : undefined}
                  association={association}
                  district={district}
                  associationCallback={(val) => setAssociation(val || undefined)}
                  districtCallback={(val) => setDistrict(val || undefined)}
                />
                <Grid item xs={12}>
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={association ? onSelectFile : undefined}
                    sx={{
                      border: '2px dashed',
                      borderColor: isDragging ? 'primary.main' : 'grey.400',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: association ? 'pointer' : 'default',
                      bgcolor: isDragging ? (thm) => alpha(thm.palette.primary.main, 0.05) : 'transparent',
                      opacity: association ? 1 : 0.5,
                      transition: 'all 0.2s ease',
                      '&:hover': association
                        ? {
                            borderColor: 'primary.main',
                            bgcolor: (thm) => alpha(thm.palette.primary.main, 0.03),
                          }
                        : {},
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 48, color: isDragging ? 'primary.main' : 'grey.500', mb: 1 }} />
                    <Typography variant='body1' sx={{ fontWeight: 500 }}>
                      {association ? t('member:importMember.dropzoneText') : t('member:importMember.selectAssociationFirst')}
                    </Typography>
                    {association && (
                      <>
                        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                          {t('member:importMember.dropzoneHint')}
                        </Typography>
                        <Button variant='contained' disableElevation sx={{ mt: 2 }} startIcon={<CloudUpload />}>
                          {t('member:importMember.selectFileButton')}
                        </Button>
                      </>
                    )}
                  </Box>
                  <input
                    id='upload-file'
                    name='upload-file'
                    type='file'
                    accept='.xlsx,.xls,.csv'
                    style={{ display: 'none' }}
                    ref={fileUploadRef}
                    onChange={changeHandler}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {/* Steg 1: Mappa kolumner */}
        {activeStep === 1 && rawExcelHeaders.length > 0 && (
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12}>
              <MemberImportColumnMapping
                excelHeaders={rawExcelHeaders}
                mappings={columnMappings}
                onMappingsChange={setColumnMappings}
                previewRows={rawExcelRows.slice(0, 3)}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' onClick={resetAll}>
                {t('common:button.cancel')}
              </Button>
              <Button variant='contained' disableElevation disabled={!hasAllRequired} startIcon={<NavigateNext />} onClick={handleProceedFromMapping}>
                {t('member:importMember.stepPreview')}
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Steg 2: Förhandsgranska */}
        {activeStep === 2 && displayData.length > 0 && (
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12}>
              <Typography gutterBottom variant='h6'>
                {t('member:importMember.previewHeader')}{' '}
                <small>
                  ({displayData.length} {t('member:importMember.rows')})
                </small>
              </Typography>
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rowHeight={30}
                  density='compact'
                  rows={displayData}
                  columns={gridColumns}
                  pagination
                  initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                  pageSizeOptions={[50]}
                  disableRowSelectionOnClick
                />
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' onClick={() => setActiveStep(1)}>
                {t('common:button.back')}
              </Button>
              <Button variant='contained' disableElevation startIcon={<VerifiedUser />} onClick={handleValidate}>
                {t('member:importMember.verifyButton')}
              </Button>
            </Grid>
          </Grid>
        )}

        {/* Steg 3: Verifieringsresultat */}
        {activeStep === 3 && validationResult && (
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12}>
              {validationResult.canImport && validationResult.warningCount === 0 && (
                <Alert severity='success' sx={{ mb: 2 }} icon={<CheckCircle />}>
                  <AlertTitle>{t('member:importMember.validationSuccess')}</AlertTitle>
                  {validationResult.totalRows} {t('member:importMember.rows')}
                </Alert>
              )}
              {validationResult.canImport && validationResult.warningCount > 0 && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  <AlertTitle>{t('member:importMember.validationHasWarnings')}</AlertTitle>
                  {validationResult.warningCount} {t('member:importMember.warnings')}
                </Alert>
              )}
              {!validationResult.canImport && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  <AlertTitle>{t('member:importMember.validationFailed')}</AlertTitle>
                  {validationResult.errorCount} {t('member:importMember.errors')}, {validationResult.warningCount} {t('member:importMember.warnings')}
                </Alert>
              )}
            </Grid>

            {/* Fix suggestions */}
            {validationResult.fixSuggestions && validationResult.fixSuggestions.length > 0 && (
              <Grid item xs={12}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {t('member:importMember.fixSuggestionsHeader')}
                </Typography>
                {validationResult.fixSuggestions.map((fix) => (
                  <Alert
                    key={fix.fixType}
                    severity='info'
                    sx={{ mb: 1 }}
                    action={(() => {
                      if (fix.fixType === 'generateMemberNr') return undefined;
                      if (fix.fixType === 'setAdmissionDate')
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              type='date'
                              size='small'
                              value={admDateInput}
                              onChange={(e) => setAdmDateInput(e.target.value)}
                              sx={{ width: 160, bgcolor: 'white' }}
                            />
                            <Button size='small' variant='outlined' disabled={!admDateInput} onClick={handleFixAdmissionDate}>
                              {t('member:importMember.fixApply')}
                            </Button>
                          </Box>
                        );
                      return (
                        <Button
                          size='small'
                          variant='outlined'
                          startIcon={<AutoFixHigh />}
                          onClick={() => {
                            if (fix.fixType === 'fixZipcode') handleFixZipcode();
                            if (fix.fixType === 'fixPhone') handleFixPhone();
                          }}
                        >
                          {t('member:importMember.fixApply')}
                        </Button>
                      );
                    })()}
                  >
                    {fix.message} ({fix.affectedRows} {t('member:importMember.rows')})
                  </Alert>
                ))}
              </Grid>
            )}

            {/* Per-rad-fel */}
            {validationResult.rows.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {validationResult.rows.map((row) => (
                    <Box key={row.rowNumber} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {t('member:importMember.row')} {row.rowNumber}
                      </Typography>
                      {row.issues.map((issue) => (
                        <Typography
                          key={`${row.rowNumber}-${issue.field}`}
                          variant='body2'
                          color={issue.severity === 'error' ? 'error' : 'warning.dark'}
                          sx={{ ml: 1 }}
                        >
                          • {issue.field}: {issue.message}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            {/* DataGrid med cellmarkering */}
            {displayData.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ height: 400, width: '100%' }}>
                  <DataGrid
                    rowHeight={30}
                    density='compact'
                    rows={displayData}
                    columns={gridColumns.map((col) => ({
                      ...col,
                      cellClassName: (params) => getCellClassName(col.field, params.id as number),
                    }))}
                    pagination
                    initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                    pageSizeOptions={[50]}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' onClick={() => setActiveStep(2)}>
                {t('common:button.back')}
              </Button>
              <Button
                variant='contained'
                color='success'
                disableElevation
                disabled={!validationResult.canImport}
                startIcon={<Save />}
                onClick={postImportToServer}
              >
                {t('member:importMember.importButton')}
              </Button>
              <MemberImportCancelButton callback={resetAll} useConfirm={validationResult.canImport} />
            </Grid>
          </Grid>
        )}
      </Box>
    </FormLoaderWrapper>
  );
};
